'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Step {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: string;
}

const steps: Step[] = [
  {
    id: 'step_1',
    number: '01',
    title: 'Input Job Details',
    description:
      'Enter basic information: job type, location, equipment specs. Our AI instantly pulls relevant market data.',
    icon: 'PencilSquareIcon',
  },
  {
    id: 'step_2',
    number: '02',
    title: 'AI Calculates Pricing',
    description:
      'Our behavioral engine analyzes real-time parts pricing, local labor rates, and historical contractor data.',
    icon: 'CpuChipIcon',
  },
  {
    id: 'step_3',
    number: '03',
    title: 'Send Professional Quote',
    description:
      'Review, adjust margins, and send a polished quote to your customer in seconds via email or SMS.',
    icon: 'PaperAirplaneIcon',
  },
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(
              entry.target.getAttribute('data-step') || '0'
            );
            setActiveStep(index);
          }
        });
      },
      { rootMargin: '-40% 0% -40% 0%' }
    );

    const stepElements = document.querySelectorAll('.workflow-step');
    stepElements.forEach((el) => observer.observe(el));

    return () => {
      stepElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-padding bg-white border-y border-border relative"
    >
      <div className="container-custom">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full border-2 border-primary flex items-center justify-center text-xs md:text-sm font-bold text-primary">
              1
            </div>
            <div className="w-8 md:w-12 h-px bg-border" />
            <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              How It Works
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight text-foreground mb-3 md:mb-4 px-4">
            Quote in <span className="text-primary">3 Simple Steps</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            From job input to customer quote in under 3 minutes
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
          {/* Steps Column */}
          <div className="space-y-8 md:space-y-12 lg:space-y-16">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`workflow-step transition-all duration-500 ${
                  activeStep === index ? 'opacity-100' : 'opacity-30'
                }`}
                data-step={index}
              >
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div
                      className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all ${
                        activeStep === index
                          ? 'bg-primary text-primary-foreground shadow-lg'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon
                        name={step.icon as any}
                        size={24}
                        variant="outline"
                        className="md:w-7 md:h-7"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs md:text-sm font-mono-numbers text-muted-foreground mb-1 md:mb-2 block">
                      {step.number}
                    </span>
                    <h3
                      className={`text-xl sm:text-2xl md:text-3xl font-heading font-bold mb-2 md:mb-3 transition-colors ${
                        activeStep === index
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sticky Visual Column */}
          <div className="lg:sticky lg:top-32 h-[400px] md:h-[500px] flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square bg-gradient-to-br from-muted to-background rounded-2xl border border-border shadow-2xl p-6 md:p-8 flex items-center justify-center">
              {/* Step 1 Visual */}
              {activeStep === 0 && (
                <div className="absolute inset-0 flex items-center justify-center p-8 md:p-12 transition-all duration-700">
                  <div className="w-full bg-white rounded-xl border border-border p-4 md:p-6 shadow-lg">
                    <div className="space-y-3 md:space-y-4">
                      <div className="h-8 md:h-10 bg-muted rounded animate-pulse" />
                      <div className="h-8 md:h-10 bg-muted rounded animate-pulse delay-100" />
                      <div className="h-8 md:h-10 bg-muted rounded animate-pulse delay-200" />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 Visual */}
              {activeStep === 1 && (
                <div className="absolute inset-0 flex items-center justify-center p-8 md:p-12 transition-all duration-700">
                  <div className="relative">
                    <Icon
                      name="CpuChipIcon"
                      size={80}
                      variant="outline"
                      className="text-primary animate-pulse md:w-[120px] md:h-[120px]"
                    />
                    <div className="absolute -top-2 md:-top-4 -right-2 md:-right-4 w-6 h-6 md:w-8 md:h-8 bg-success rounded-full animate-ping" />
                  </div>
                </div>
              )}

              {/* Step 3 Visual */}
              {activeStep === 2 && (
                <div className="absolute inset-0 flex items-center justify-center p-8 md:p-12 transition-all duration-700">
                  <div className="w-full bg-white rounded-xl border border-border p-4 md:p-6 shadow-lg">
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        Quote Ready
                      </span>
                      <div className="w-2 h-2 bg-success rounded-full animate-pulse-glow" />
                    </div>
                    <div className="text-3xl md:text-4xl font-mono-numbers font-bold text-primary mb-2">
                      $1,827
                    </div>
                    <div className="h-2 bg-success/20 rounded-full overflow-hidden">
                      <div className="h-full bg-success rounded-full w-full animate-pulse" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}